import React, { useEffect } from 'react';
import { CheckCircle, XCircle, X } from 'lucide-react';

interface NotificationProps {
  isOpen: boolean;
  message: string;
  type: 'success' | 'error';
  onClose: () => void;
}

const Notification: React.FC<NotificationProps> = ({ isOpen, message, type, onClose }) => {
  
  // Auto-hide after 3 seconds
  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        onClose();
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed top-5 left-1/2 transform -translate-x-1/2 z-[110] animate-slide-in-down">
      <div 
        className={`flex items-center gap-3 px-6 py-4 rounded-lg shadow-2xl border-l-4 min-w-[320px] max-w-md
        ${type === 'success' 
            ? 'bg-white border-green-500 text-gray-800' 
            : 'bg-white border-red-500 text-gray-800'
        }`}
      >
        {/* Icon */}
        <div className="flex-shrink-0">
          {type === 'success' ? (
            <CheckCircle className="w-6 h-6 text-green-500" />
          ) : (
            <XCircle className="w-6 h-6 text-red-500" />
          )}
        </div>

        {/* Message */}
        <div className="flex-grow">
          <h4 className="font-bold text-sm uppercase tracking-wide mb-0.5">
            {type === 'success' ? 'Success' : 'Error'}
          </h4>
          <p className="text-sm text-gray-600 font-medium">
            {message}
          </p>
        </div>

        {/* Close Button */}
        <button 
          onClick={onClose} 
          className="text-gray-400 hover:text-gray-600 transition-colors"
        >
          <X size={18} />
        </button>
      </div>

      {/* Tailwind Animation Config Note:
         Make sure your tailwind.config.js has keyframes for 'slide-in-down'.
         If not, you can use simple 'transition-all' classes instead.
      */}
      <style>{`
        @keyframes slideInDown {
          from { opacity: 0; transform: translate(-50%, -20px); }
          to { opacity: 1; transform: translate(-50%, 0); }
        }
        .animate-slide-in-down {
          animation: slideInDown 0.4s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

export default Notification;